/**
 * Pattern / colorblock CMS ids come from `GET /api/v4/print-config`.
 * Hardcoded numbers are only used until that catalog loads, and for proofs saved
 * against the original CMS uids (method 11, treatments 8/9, full garment 86).
 */

export interface IPatternColorblockIds {
  methodId: number;
  colorBlockTreatmentId: number;
  customPatternTreatmentId: number;
  fullGarmentPlacementId: number;
}

export const FALLBACK_PATTERN_COLORBLOCK_IDS: IPatternColorblockIds = {
  methodId: 11,
  colorBlockTreatmentId: 8,
  customPatternTreatmentId: 9,
  fullGarmentPlacementId: 86,
};

export const PATTERNS_AND_COLORBLOCK_TYPE = 'patternsAndorColorblock';
export const PATTERNS_AND_COLORBLOCK_LABEL = 'Patterns and/or Colorblock';

export interface IPrintConfigPattern {
  uid?: number;
  label?: string;
  colorName?: string;
}

export interface IPrintConfigPlacement {
  uid?: number;
  label?: string;
  locationType?: string;
}

export interface IPrintConfigDecoratingMethod {
  uid?: number;
  type?: string;
  label?: string;
  patterns?: IPrintConfigPattern[];
  placement?: IPrintConfigPlacement[];
}

export interface IFullGarmentPart {
  id?: number;
  name?: string | null;
}

let catalog: IPatternColorblockIds = { ...FALLBACK_PATTERN_COLORBLOCK_IDS };

const uniquePositiveIds = (ids: number[]): number[] =>
  ids.filter((id, index) => Number.isFinite(id) && id > 0 && ids.indexOf(id) === index);

const normalize = (value: string | null | undefined): string =>
  (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

export const isPatternsAndColorblockLabel = (value?: string | null): boolean => {
  const label = normalize(value);

  return label === 'patterns and/or colorblock' || label === 'patterns and or colorblock';
};

const isColorBlockLabel = (value?: string | null): boolean => {
  const label = normalize(value);

  return label === 'color block' || label === 'colorblock';
};

const isCustomPatternLabel = (value?: string | null): boolean => {
  const label = normalize(value);

  return label === 'custom pattern';
};

const isFullGarmentPlacement = (placement: IPrintConfigPlacement): boolean =>
  normalize(placement.locationType) === 'full_garment' ||
  normalize(placement.label) === 'full garment';

const asDecoratingMethods = (value: unknown): IPrintConfigDecoratingMethod[] =>
  Array.isArray(value) ? (value as IPrintConfigDecoratingMethod[]) : [];

const findPatternsAndColorblockMethod = (
  methods: IPrintConfigDecoratingMethod[]
): IPrintConfigDecoratingMethod | undefined =>
  methods.find(
    (method) =>
      method.type === PATTERNS_AND_COLORBLOCK_TYPE ||
      isPatternsAndColorblockLabel(method.label) ||
      isPatternsAndColorblockLabel(method.type)
  );

const positiveUid = (value: unknown): number | null => {
  const uid = Number(value);

  return Number.isFinite(uid) && uid > 0 ? uid : null;
};

export const resolvePatternColorblockIds = (decoratingMethods: unknown): IPatternColorblockIds => {
  const ids = { ...FALLBACK_PATTERN_COLORBLOCK_IDS };
  const method = findPatternsAndColorblockMethod(asDecoratingMethods(decoratingMethods));

  if (!method) {
    return ids;
  }

  const methodId = positiveUid(method.uid);

  if (methodId !== null) {
    ids.methodId = methodId;
  }

  (method.patterns ?? []).forEach((pattern) => {
    const uid = positiveUid(pattern.uid);
    const label = pattern.label ?? pattern.colorName;

    if (uid === null) {
      return;
    }

    if (isColorBlockLabel(label)) {
      ids.colorBlockTreatmentId = uid;
    }

    if (isCustomPatternLabel(label)) {
      ids.customPatternTreatmentId = uid;
    }
  });

  const fullGarment = (method.placement ?? []).find(
    (placement) => positiveUid(placement.uid) !== null && isFullGarmentPlacement(placement)
  );
  const fullGarmentUid = fullGarment ? positiveUid(fullGarment.uid) : null;

  if (fullGarmentUid !== null) {
    ids.fullGarmentPlacementId = fullGarmentUid;
  }

  return ids;
};

export const getPatternColorblockIds = (): IPatternColorblockIds => catalog;

/** Colorblock first so quoting emits treatments in the same order as the fallback constants. */
export const getPatternsAndColorblockTreatmentIds = (): number[] =>
  uniquePositiveIds([
    catalog.colorBlockTreatmentId,
    catalog.customPatternTreatmentId,
    FALLBACK_PATTERN_COLORBLOCK_IDS.colorBlockTreatmentId,
    FALLBACK_PATTERN_COLORBLOCK_IDS.customPatternTreatmentId,
  ]);

/** Custom pattern first so a part on both treatments keeps the pattern swatch when pills merge. */
export const getPatternColorblockMergeTreatmentIds = (): number[] =>
  uniquePositiveIds([
    catalog.customPatternTreatmentId,
    FALLBACK_PATTERN_COLORBLOCK_IDS.customPatternTreatmentId,
    catalog.colorBlockTreatmentId,
    FALLBACK_PATTERN_COLORBLOCK_IDS.colorBlockTreatmentId,
  ]);

export const isFullGarmentPart = (part: IFullGarmentPart): boolean => {
  const id = Number(part.id);

  return (
    id === catalog.fullGarmentPlacementId ||
    id === FALLBACK_PATTERN_COLORBLOCK_IDS.fullGarmentPlacementId ||
    normalize(part.name) === 'full garment'
  );
};

export const applyPrintConfigPatternIds = (decoratingMethods: unknown): IPatternColorblockIds => {
  catalog = resolvePatternColorblockIds(decoratingMethods);

  return catalog;
};

export const resetPatternColorblockIdsForTests = (): void => {
  catalog = { ...FALLBACK_PATTERN_COLORBLOCK_IDS };
};
